import React, { useContext, useState } from 'react';

import { useFulfillmentLifecycleData } from '~/components/logistics/fulfillmentLifecycle/hooks/useFulfillmentLifecycleData';
import RouteCard from '~/components/logistics/fulfillmentLifecycle/components/RouteCard';
import { UserContext } from '~/contexts/UserContext';
import { Alert, AlertDescription, AlertTitle } from '~/shadcn/components/ui/Alert';
import { Card, CardContent, CardHeader } from '~/shadcn/components/ui/Card';
import { Skeleton } from '~/shadcn/components/ui/Skeleton';
import dayjs from '~/utils/dayjsConfig';

/**
 * FulfillmentLifecycleDashboard
 *
 * Main container component for the Fulfillment Lifecycle dashboard.
 * Displays route status, risk indicators, and throughput analytics.
 *
 * State:
 * - selectedDate: Operating date for the dashboard (default: today)
 * - filters: Active filter selections (deliveryTypes, orderSources, etc.)
 *
 * Data fetching handled by useFulfillmentLifecycleData hook.
 */
function FulfillmentLifecycleDashboard() {
    const { getFacilityID, getFacilityName } = useContext(UserContext);
    const facilityId = getFacilityID();
    const facilityName = getFacilityName();

    // State for date selection and filters
    // TODO: Add date picker control in Ticket 3c (will use setSelectedDate)
    const [selectedDate] = useState(dayjs().format('YYYY-MM-DD'));
    // TODO: Implement filter controls in Ticket 3c
    const [filters] = useState({
        orderSources: [],
        deliveryTypes: [],
        statusFilter: 'all', // 'all' | 'critical' | 'atRisk' | 'onTrack'
        milestone: 'all', // 'all' | 'labels' | 'packing' | 'loading' | 'complete'
    });

    // Fetch dashboard data
    // TODO: Wire up refetch for manual refresh in Ticket 3c
    const { error, loading, routes, summary } = useFulfillmentLifecycleData(facilityId, selectedDate, {
        orderSources: filters.orderSources,
        deliveryTypes: filters.deliveryTypes,
    });

    // Render loading skeleton
    if (loading && routes.length === 0) {
        return (
            <div className="fulfillment-lifecycle-dashboard p-6 space-y-6">
                <DashboardHeaderSkeleton />
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                    {[1, 2, 3, 4, 5, 6].map((i) => (
                        <RouteCardSkeleton key={i} />
                    ))}
                </div>
            </div>
        );
    }

    // Render error state
    if (error) {
        return (
            <div className="fulfillment-lifecycle-dashboard p-6">
                <Alert variant="destructive">
                    <AlertTitle>Error loading dashboard</AlertTitle>
                    <AlertDescription>{error}</AlertDescription>
                </Alert>
            </div>
        );
    }

    // Render empty state
    if (!loading && routes.length === 0) {
        return (
            <div className="fulfillment-lifecycle-dashboard p-6 space-y-6">
                <DashboardHeader facilityName={facilityName} selectedDate={selectedDate} />
                <Alert>
                    <AlertTitle>No routes found</AlertTitle>
                    <AlertDescription>
                        No routes found for {facilityName} on {dayjs(selectedDate).format('MMMM D, YYYY')}.
                    </AlertDescription>
                </Alert>
            </div>
        );
    }

    return (
        <div className="fulfillment-lifecycle-dashboard p-6 space-y-6">
            {/* Header */}
            <DashboardHeader facilityName={facilityName} selectedDate={selectedDate} />

            {/* Summary Alerts */}
            <SummaryAlerts summary={summary} />

            {/* Routes Grid - Placeholder for Ticket 3e */}
            <div className="space-y-4">
                <h2 className="text-lg font-semibold">Routes ({routes.length})</h2>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                    {routes.map((route) => (
                        <RouteCard key={route.lineHaulId} route={route} />
                    ))}
                </div>
            </div>
        </div>
    );
}

/**
 * Dashboard header with title and date info
 * TODO: Replace with full DashboardHeader component in Ticket 3c (add date picker)
 */
function DashboardHeader({ facilityName, selectedDate }) {
    return (
        <div className="space-y-2">
            <h1 className="text-2xl font-bold">Shipment Status Dashboard</h1>
            <p className="text-muted-foreground">
                {facilityName} &bull; {dayjs(selectedDate).format('MMMM D, YYYY')} &bull; Monitoring warehouse flow and
                pull time compliance
            </p>
        </div>
    );
}

/**
 * Summary alerts showing critical/at-risk counts
 * TODO: Replace with full SummaryAlerts component in Ticket 3c
 */
function SummaryAlerts({ summary }) {
    return (
        <div className="flex gap-4 flex-wrap">
            <div className="flex items-center gap-2 px-4 py-2 bg-red-50 border border-red-200 rounded-lg">
                <span className="text-2xl font-bold text-red-600">{summary.critical}</span>
                <span className="text-red-700">Critical Issues</span>
            </div>
            <div className="flex items-center gap-2 px-4 py-2 bg-yellow-50 border border-yellow-200 rounded-lg">
                <span className="text-2xl font-bold text-yellow-600">{summary.atRisk}</span>
                <span className="text-yellow-700">At Risk</span>
            </div>
            <div className="flex items-center gap-2 px-4 py-2 bg-green-50 border border-green-200 rounded-lg">
                <span className="text-2xl font-bold text-green-600">{summary.onTrack}</span>
                <span className="text-green-700">On Track</span>
            </div>
            <div className="flex items-center gap-2 px-4 py-2 bg-slate-50 border border-slate-200 rounded-lg">
                <span className="text-sm text-slate-600">
                    {summary.totalRoutes} routes &bull; {summary.totalShipments} shipments &bull;{' '}
                    {summary.facilityThroughput}/hr throughput
                </span>
            </div>
        </div>
    );
}

/**
 * Skeleton for dashboard header during loading
 */
function DashboardHeaderSkeleton() {
    return (
        <div className="space-y-2">
            <Skeleton className="h-8 w-64" />
            <Skeleton className="h-4 w-96" />
        </div>
    );
}

/**
 * Skeleton for route card during loading
 */
function RouteCardSkeleton() {
    return (
        <Card>
            <CardHeader className="pb-2">
                <Skeleton className="h-5 w-16" />
                <Skeleton className="h-6 w-12 mt-2" />
                <Skeleton className="h-4 w-20" />
            </CardHeader>
            <CardContent className="space-y-3">
                <Skeleton className="h-4 w-32" />
                <Skeleton className="h-4 w-48" />
                <Skeleton className="h-2 w-full" />
                <div className="grid grid-cols-3 gap-2">
                    <Skeleton className="h-8" />
                    <Skeleton className="h-8" />
                    <Skeleton className="h-8" />
                </div>
            </CardContent>
        </Card>
    );
}

export default FulfillmentLifecycleDashboard;
